import { CreditCardBrandEnum } from 'enums'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import media from 'styles/media'
import { ExpiredContainer, ExpiredLabel } from './styles'
import { LabelSmall } from 'components/base-ui/FontsV2'
import { FontSize } from 'styles/fontSize'
import { FontWeight } from 'styles/fontWeight'

interface CardHolderNameStyle {
  size: keyof FontSize
  weight: keyof FontWeight
}

interface CreditCardDetailsProps {
  cardBrand: string
  cardholderName: string
  cardLastDigits: string
  hasExpired: boolean
  forceActive?: boolean
  holderNameStyle?: CardHolderNameStyle
}

export default function CreditCardDetails({
  cardBrand,
  cardholderName,
  cardLastDigits,
  hasExpired,
  forceActive = false,
  holderNameStyle = { size: 'md', weight: 'regular' },
}: CreditCardDetailsProps) {
  const { t } = useTranslation()
  const disabledDetail = !forceActive && hasExpired

  return (
    <CardDetailsWrapper>
      <CardDetail disabled={disabledDetail}>
        <CardType src={getIconCreditCardByBrand(cardBrand)} alt="icon-brand-credit-card" />
        <Truncate $style={holderNameStyle}>{cardholderName}</Truncate>***{cardLastDigits}
      </CardDetail>
      {hasExpired && (
        <ExpiredContainer>
          <ExpiredLabel noWrap>{t('paymentMethodPage.creditCard.expired')}</ExpiredLabel>
        </ExpiredContainer>
      )}
    </CardDetailsWrapper>
  )
}

function getIconCreditCardByBrand(brand: string) {
  switch (brand) {
    case CreditCardBrandEnum.Visa:
      return `${process.env.PUBLIC_URL}/assets/payment/visa.svg`
    case CreditCardBrandEnum.MasterCard:
      return `${process.env.PUBLIC_URL}/assets/payment/master-card.svg`
    case CreditCardBrandEnum.JCB:
      return `${process.env.PUBLIC_URL}/assets/payment/jcb.svg`
    case CreditCardBrandEnum.AMEX:
      return `${process.env.PUBLIC_URL}/assets/payment/amex.svg`
    case CreditCardBrandEnum.UnionPay:
      return `${process.env.PUBLIC_URL}/assets/payment/unionpay.svg`
  }
}

const CardDetailsWrapper = styled.span`
  display: flex;
  align-items: center;
`

const CardDetail = styled.span<{
  disabled: boolean
}>`
  display: flex;
  align-items: center;
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`

const CardType = styled.img`
  margin-right: ${({ theme }) => theme.spacing.S};
  max-width: 30px;
`

const Truncate = styled(LabelSmall)<{ $style: CardHolderNameStyle }>`
  font-size: ${({ theme, $style }) => theme.fontSize[$style.size]};
  font-weight: ${({ theme, $style }) => theme.fontWeight[$style.weight]};
  max-width: 6rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;

  @media (${media.screenSmMiniMax}) {
    margin-right: 4px;
    max-width: 4rem;
  }

  @media (${media.screenXsMax}) {
    margin-right: 2px;
    max-width: 3rem;
  }
`
